import { useState } from "react";
import { UserPlus } from "lucide-react";
import { Topbar } from "@marekl/adminer/shell";
import {
  Page,
  Table,
  Badge,
  Button,
  Text,
  DateTimeField,
  TextField,
} from "@marekl/adminer/components";
import { useUsers } from "../../auth/queries";
import type { User } from "../../auth/queries";
import { CreateUserModal, EditUserModal, ResetPasswordModal, RoleBadge } from "./UserModals";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; user: User }
  | { kind: "reset"; user: User };

export function UsersPage() {
  const { data: users = [], isLoading } = useUsers();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });

  function closeModal() {
    setModal({ kind: "closed" });
  }

  return (
    <>
      <Topbar crumbs={[{ label: "Uživatelé" }]} />
      <Page.Content>
        <Page.Header
          title="Uživatelé"
          subtitle="správa uživatelských účtů"
          actions={
            <Button
              variant="primary"
              onClick={() => setModal({ kind: "create" })}
            >
              <UserPlus size={16} aria-hidden="true" />
              Přidat uživatele
            </Button>
          }
        />

        <Page.SectionHeader
          title="Uživatelé systému"
          meta={`${users.length} ${users.length === 1 ? "účet" : users.length >= 2 && users.length <= 4 ? "účty" : "účtů"}`}
        />

        <Table<User>
          rowKey={(u) => u.id}
          columns={[
            {
              key: "username",
              header: "Uživ. jméno",
              render: (u) => <Text variant="mono">{u.username}</Text>,
            },
            {
              key: "display_name",
              header: "Jméno",
              render: (u) => <TextField record={u} source="display_name" />,
            },
            {
              key: "role",
              header: "Role",
              render: (u) => <RoleBadge role={u.role} />,
            },
            {
              key: "email",
              header: "E-mail",
              render: (u) => <TextField record={u} source="email" />,
            },
            {
              key: "active",
              header: "Stav",
              render: (u) =>
                u.active ? (
                  <Badge variant="ok">Aktivní</Badge>
                ) : (
                  <Badge variant="muted">Deaktivován</Badge>
                ),
            },
            {
              key: "last_login_at",
              header: "Posl. přihlášení",
              render: (u) => <DateTimeField record={u} source="last_login_at" />,
            },
            {
              key: "actions",
              header: "",
              render: (u) => (
                <Table.Actions>
                  <Table.Action
                    onClick={() => setModal({ kind: "edit", user: u })}
                  >
                    Upravit
                  </Table.Action>
                  <Table.Action
                    onClick={() => setModal({ kind: "reset", user: u })}
                  >
                    Heslo
                  </Table.Action>
                </Table.Actions>
              ),
            },
          ]}
          rows={users}
          empty={
            isLoading ? "Načítám…" : "Žádní uživatelé."
          }
        />
      </Page.Content>

      <CreateUserModal
        open={modal.kind === "create"}
        onClose={closeModal}
      />

      {modal.kind === "edit" && (
        <EditUserModal
          user={modal.user}
          onClose={closeModal}
        />
      )}

      {modal.kind === "reset" && (
        <ResetPasswordModal
          user={modal.user}
          onClose={closeModal}
        />
      )}
    </>
  );
}
